/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect } from 'react';
import { Card, Col, Modal, Row, Table } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { DoughnutChart } from 'components/charts';
import { Button } from 'atoms';
import { LoadingAnimation } from 'components/common';

const CardContainer = styled(Card)`
	min-height: 300px;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const TitleText = styled.div`
	font-weight: 500;
	font-size: 16px;
`;

const ViewMore = styled(Button)`
	color: #ee6055;
	float: right;
	background: #fff6f5;
`;

const ChartWRapper = styled.div`
	height: 240px;
	margin: 16px 0;
`;

const EmptyChart = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 30px;
	font-weight: 500;
	min-height: 95px;
`;

const columns = [
	{
		title: 'City',
		dataIndex: 'city',
		key: 'city',
	},
	{
		title: 'City Count',
		dataIndex: 'count',
		key: 'count',
	},
	{
		title: 'Percentage',
		dataIndex: 'percent',
		key: 'percent',
	},
];

function InterCityTrips({ cities, isLoading }) {
	const [modalVisible, setModalVisible] = useState(false);
	const [donutList, setDonutList] = useState();
	const showModal = () => {
		setModalVisible(true);
	};

	const handleCancel = () => {
		setModalVisible(false);
	};

	useEffect(() => {
		if (cities) {
			const intercityData = cities ? cities.cities : [];
			if (intercityData?.length) {
				const getDonutListValue =
					intercityData?.length > 3 ? intercityData.slice(0, 3) : intercityData;

				if (intercityData?.length > 3) {
					const sumGetDonutListValue =
						100 -
						getDonutListValue
							.map((res1) => res1.percent)
							.reduce((acc, cur) => acc + cur);
					const otherCount = intercityData
						.slice(3, intercityData.length)
						.map((res3) => res3.count)
						.reduce((total, item) => total + item, 0);
					const finalDonutList = [
						...getDonutListValue,
						{
							percent: sumGetDonutListValue,
							count: otherCount,
							city: 'Others',
						},
					];
					setDonutList(finalDonutList);
				} else {
					setDonutList(getDonutListValue);
				}
			}
		} else {
			setDonutList();
		}
	}, [cities]);

	const donutData = {
		labels: donutList && donutList.map((res) => `${res.city} ${res.percent}%`),
		datasets: [
			{
				totalCount: donutList && donutList.map((res) => res.count),
				label: 'InterCity Trip Ratio',
				data: donutList && donutList.map((res) => res.percent),
				backgroundColor: ['#EE6055', '#A1EF7A', '#70D6FF', '#9381FF'],
				hoverOffset: 4,
			},
		],
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			datalabels: {
				color: 'white',
				font: {
					size: '23%',
					weight: 500,
				},
			},
			legend: {
				position: 'right',
				labels: {
					boxWidth: 15,
					boxHeight: 15,
				},
				onClick: (e) => {
					// eslint-disable-next-line vars-on-top, no-var, no-redeclare
					if (!e) var e = window.event;
					e.cancelBubble = true;
					if (e.stopPropagation) e.stopPropagation();
				},
			},
			tooltip: {
				enabled: true,
				usePointStyle: true,
				callbacks: {
					// To change title in tooltip
					// eslint-disable-next-line arrow-body-style
					title: (data) => {
						return data[0].parsed.x;
					},
					// To change label in tooltip
					// eslint-disable-next-line arrow-body-style
					label: (data) => {
						return ` Trip Ratio Count: ${data.dataset.totalCount[data.dataIndex]}`;
					},
				},
			},
		},
	};

	return (
		<CardContainer>
			{isLoading ? (
				<LoadingAnimation visible={isLoading} animationID="IntercityTrips" />
			) : (
				<Row align="middle" justify="space-between">
					{donutList ? (
						<>
							<Col xs={24} sm={12} md={12}>
								<TitleText>Intercity Trip Ratio</TitleText>
							</Col>
							<Col xs={24} sm={12} md={12}>
								<ViewMore onClick={showModal} overflow="auto">
									View More
									<EyeOutlined style={{ marginLeft: '10px' }} />
								</ViewMore>
							</Col>
							<Col xs={24} sm={24} md={24}>
								<ChartWRapper>
									<DoughnutChart
										ChartOptions={chartOptions}
										chartData={donutData}
									/>
								</ChartWRapper>
							</Col>
							{cities && cities.cities.length > 0 && (
								<Modal
									title="Intercity Trip Ratio"
									open={modalVisible}
									onCancel={handleCancel}
									footer={null}
									bodyStyle={{ maxHeight: '500px', overflow: 'auto' }}
								>
									<Table
										dataSource={cities.cities}
										columns={columns}
										pagination={false}
										scroll={{ y: 240 }}
									/>
								</Modal>
							)}
						</>
					) : (
						<>
							<Col xs={24} sm={24} md={24}>
								<TitleText>Intercity Trip Ratio</TitleText>
							</Col>
							<Col xs={24} sm={24} md={24}>
								<EmptyChart>0</EmptyChart>
							</Col>
						</>
					)}
				</Row>
			)}
		</CardContainer>
	);
}

InterCityTrips.propTypes = {
	cities: PropTypes.object,
	isLoading: PropTypes.bool,
};

export default InterCityTrips;
